/**
 * CX Data Foundation — Session 19A
 *
 * Seed data for all CX Firestore collections:
 *   cx_cases            — 15 support cases in mixed statuses
 *   cx_templates        — 12 communication templates with merge fields
 *   customer_contacts   — 13 contacts across 5 customers
 *   cx_messages         — 20 unified-inbox messages
 *   surveys             — 10 post-delivery survey responses
 *
 * All documents comply with the 13-point schema standard:
 *   tenantId, createdAt, createdBy, updatedAt, updatedBy,
 *   deletedAt, isDeleted, version, source (+ domain fields)
 *
 * Customer IDs  : cu-001…cu-005  (Steve Madden, Caldwell Casting,
 *                                  Century Aluminum, McKesson Medical, Wild Fork Foods)
 * Load IDs      : load-00001…load-00050 (from seedDataExpanded)
 * User IDs      : user-001 (Randall Roberson), user-002 (Maria Rodriguez)
 */

const U = {
  tenantId: 'watchmen',
  createdBy: 'system',
  updatedBy: 'system',
  deletedAt: null,
  isDeleted: false,
  version: 1,
  source: 'seed',
};

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — CX_CASES (15 total)
//   3 damage claims · 2 late delivery · 5 general inquiry
//   2 billing dispute · 1 lost shipment · 2 claim (filed/resolved)
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_CX_CASES = [
  // ── Damage Claims (3 open) ────────────────────────────────────────────────
  {
    ...U,
    id: 'case-001',
    caseId: 'CASE-00001',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    loadId: 'load-00005',
    subject: 'Pallet damage — footwear shipment WC-10247',
    type: 'damage',
    status: 'open',
    priority: 'urgent',
    assignedTo: 'user-002',
    description: 'Customer reports 2 of 18 pallets received with visible crush damage. Product was stacked improperly during transit. SKUs SHOE-4401 and SHOE-4402 affected. Customer requesting full replacement or credit.',
    resolution: null,
    claimAmount: 8400,
    claimStatus: 'filed',
    createdAt: '2026-04-13T08:30:00Z',
    updatedAt: '2026-04-15T14:00:00Z',
  },
  {
    ...U,
    id: 'case-002',
    caseId: 'CASE-00002',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    loadId: 'load-00012',
    subject: 'Temperature excursion — frozen beef shipment WC-10255',
    type: 'damage',
    status: 'in_progress',
    priority: 'urgent',
    assignedTo: 'user-001',
    description: 'IoT tracker IOTK-001 recorded temperature breach from -8°F to +28°F over 4 hours. Product integrity compromised on 12 cases of Wagyu beef (est. $22,800 value). Customer demanding full claim including disposal cost.',
    resolution: null,
    claimAmount: 24500,
    claimStatus: 'filed',
    createdAt: '2026-04-15T06:45:00Z',
    updatedAt: '2026-04-17T09:00:00Z',
  },
  {
    ...U,
    id: 'case-003',
    caseId: 'CASE-00003',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    loadId: 'load-00008',
    subject: 'Shock damage — cast iron components WC-10270',
    type: 'damage',
    status: 'open',
    priority: 'high',
    assignedTo: 'user-002',
    description: 'Shock sensor recorded 4.2G impact event. Customer reports 3 cast components cracked on receipt. Load WC-10270 flatbed shipment. Customer has submitted photos — requesting independent inspector before authorizing claim.',
    resolution: null,
    claimAmount: 5200,
    claimStatus: 'filed',
    createdAt: '2026-04-14T11:15:00Z',
    updatedAt: '2026-04-16T16:30:00Z',
  },

  // ── Late Delivery (2) ─────────────────────────────────────────────────────
  {
    ...U,
    id: 'case-004',
    caseId: 'CASE-00004',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    loadId: 'load-00020',
    subject: 'Rail delay — Louisville mechanical — 28hr late',
    type: 'late_delivery',
    status: 'pending_customer',
    priority: 'high',
    assignedTo: 'user-001',
    description: 'Rail car NS-662841 experienced hot bearing detection at Louisville, KY. Train held for mechanical inspection 28 hours. Customer\'s production line stopped due to missing aluminum extrusions. Customer requesting late delivery penalty waiver and expedited re-routing.',
    resolution: 'Offered $800 freight credit and expedited rail re-route via alternate BNSF train. Awaiting customer acceptance.',
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-14T20:00:00Z',
    updatedAt: '2026-04-17T10:00:00Z',
  },
  {
    ...U,
    id: 'case-005',
    caseId: 'CASE-00005',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    loadId: 'load-00031',
    subject: 'OTR delivery missed window — medical facility backup',
    type: 'late_delivery',
    status: 'resolved',
    priority: 'high',
    assignedTo: 'user-002',
    description: 'Driver arrived 6 hours late to McKesson distribution center. Receiving dock closed — next available window 24hrs later. Customer\'s regional hospital system experienced supply disruption for surgical supplies.',
    resolution: 'Freight charge credited in full ($3,200). Carrier cited and added to probation list. Customer accepted resolution 4/16.',
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-12T16:00:00Z',
    updatedAt: '2026-04-16T11:00:00Z',
  },

  // ── General Inquiries (5) ─────────────────────────────────────────────────
  {
    ...U,
    id: 'case-006',
    caseId: 'CASE-00006',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    loadId: 'load-00003',
    subject: 'ETA update request — spring collection shipment',
    type: 'general',
    status: 'resolved',
    priority: 'medium',
    assignedTo: 'user-001',
    description: 'Customer requesting live ETA update for footwear spring collection in transit from Ho Chi Minh City. Vessel ETA has shifted 3 days due to port congestion at Singapore.',
    resolution: 'Provided updated ETA of April 20, 2026. Sent tracking link and set up proactive notifications via AEGIS.',
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-13T14:00:00Z',
    updatedAt: '2026-04-14T09:30:00Z',
  },
  {
    ...U,
    id: 'case-007',
    caseId: 'CASE-00007',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    loadId: 'load-00041',
    subject: 'Customs hold status inquiry — pharmaceutical raw materials',
    type: 'general',
    status: 'in_progress',
    priority: 'urgent',
    assignedTo: 'user-001',
    description: 'WC-OCN-006 placed on customs hold at Savannah port — pharmaceutical cargo Class 6.1. Customer needs daily status updates as this material is needed for manufacturing. FDA inspection scheduled for April 19.',
    resolution: null,
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-15T08:00:00Z',
    updatedAt: '2026-04-17T08:00:00Z',
  },
  {
    ...U,
    id: 'case-008',
    caseId: 'CASE-00008',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    loadId: null,
    subject: 'New lane inquiry — Dallas to Detroit intermodal',
    type: 'general',
    status: 'open',
    priority: 'medium',
    assignedTo: 'user-001',
    description: 'Customer asking about capacity and rates for new intermodal lane: Dallas Wilmer Yard to Detroit Livernois. Monthly volume approximately 8-12 loads. Requesting lane quote and transit time.',
    resolution: null,
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-16T13:00:00Z',
    updatedAt: '2026-04-16T13:00:00Z',
  },
  {
    ...U,
    id: 'case-009',
    caseId: 'CASE-00009',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    loadId: 'load-00028',
    subject: 'Per diem dispute — Houston port container',
    type: 'general',
    status: 'pending_customer',
    priority: 'medium',
    assignedTo: 'user-002',
    description: 'Container HLXU9876543 accumulated 3 days per diem at Barbours Cut Terminal. Customer claims they were not notified of LFD approaching. Requesting per diem waiver ($450). Reviewing notification logs.',
    resolution: 'Confirmed notification was sent via email on April 13. Sending log proof to customer. Offered one-day courtesy waiver ($150).',
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-16T09:00:00Z',
    updatedAt: '2026-04-17T11:30:00Z',
  },
  {
    ...U,
    id: 'case-010',
    caseId: 'CASE-00010',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    loadId: null,
    subject: 'Carrier certification documentation request',
    type: 'general',
    status: 'closed',
    priority: 'low',
    assignedTo: 'user-002',
    description: 'Customer procurement team requesting updated carrier insurance certificates, MC numbers, and safety ratings for annual vendor review. Need documents for 6 active carriers on their lanes.',
    resolution: 'All carrier documentation compiled and sent via secure link. Customer confirmed receipt and marked case closed.',
    claimAmount: null,
    claimStatus: 'none',
    createdAt: '2026-04-10T10:00:00Z',
    updatedAt: '2026-04-12T14:00:00Z',
  },

  // ── Billing Disputes (2) ──────────────────────────────────────────────────
  {
    ...U,
    id: 'case-011',
    caseId: 'CASE-00011',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    loadId: 'load-00018',
    subject: 'Invoice dispute — accessorial charges INV-4412',
    type: 'billing',
    status: 'in_progress',
    priority: 'medium',
    assignedTo: 'user-001',
    description: 'Customer disputing $420 detention charge on INV-4412. Claims driver arrived early and was held less than 2 hours. Our records show driver on-site for 3h 22m. Reviewing dock clock-in data with carrier.',
    resolution: null,
    claimAmount: 420,
    claimStatus: 'none',
    createdAt: '2026-04-14T15:00:00Z',
    updatedAt: '2026-04-16T14:00:00Z',
  },
  {
    ...U,
    id: 'case-012',
    caseId: 'CASE-00012',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    loadId: 'load-00036',
    subject: 'Double-billed freight — air shipment FRA to JFK',
    type: 'billing',
    status: 'resolved',
    priority: 'high',
    assignedTo: 'user-002',
    description: 'Customer identified duplicate invoice for air freight shipment FRA-JFK. INV-4418 and INV-4419 both charged $11,760 for same air waybill. System error from manual entry. Confirmed duplicate billing.',
    resolution: 'Credit memo issued for $11,760. Applied to next invoice cycle. Customer satisfied. Implemented duplicate invoice check in billing workflow.',
    claimAmount: 11760,
    claimStatus: 'paid',
    createdAt: '2026-04-11T09:00:00Z',
    updatedAt: '2026-04-13T16:00:00Z',
  },

  // ── Lost Shipment (1) ─────────────────────────────────────────────────────
  {
    ...U,
    id: 'case-013',
    caseId: 'CASE-00013',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    loadId: 'load-00025',
    subject: 'Missing LTL pallet — auto components shipment',
    type: 'lost_shipment',
    status: 'in_progress',
    priority: 'high',
    assignedTo: 'user-001',
    description: 'Customer received 11 of 12 pallets on LTL delivery. Pallet 12 containing CNC-machined transmission casings ($9,800 value) is unaccounted for. Carrier tracing in progress — may be at Cleveland hub. PRO# 456789012.',
    resolution: null,
    claimAmount: 9800,
    claimStatus: 'filed',
    createdAt: '2026-04-15T16:00:00Z',
    updatedAt: '2026-04-17T07:30:00Z',
  },

  // ── Claims (2 — one approved, one denied) ────────────────────────────────
  {
    ...U,
    id: 'case-014',
    caseId: 'CASE-00014',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    loadId: 'load-00015',
    subject: 'Freight claim — moisture damage aluminum coil',
    type: 'claim',
    status: 'resolved',
    priority: 'medium',
    assignedTo: 'user-002',
    description: 'Aluminum coil received with surface rust/moisture damage. Coil was on open flatbed in heavy rain. Carrier did not apply tarp per agreement. Claim for $6,400 representing cost to recondition affected surface.',
    resolution: 'Claim approved for $5,800 (carrier partial liability — customer shared responsibility for not specifying tarp requirement in BOL). Payment processed April 10.',
    claimAmount: 6400,
    claimStatus: 'paid',
    createdAt: '2026-03-28T10:00:00Z',
    updatedAt: '2026-04-10T15:00:00Z',
  },
  {
    ...U,
    id: 'case-015',
    caseId: 'CASE-00015',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    loadId: 'load-00033',
    subject: 'Claim denied — pre-existing freeze damage',
    type: 'claim',
    status: 'closed',
    priority: 'low',
    assignedTo: 'user-001',
    description: 'Customer filed claim for $3,200 citing freezer burn on seafood product. Carrier provided photographic evidence of product condition at origin pickup showing pre-existing freeze damage. Claim denied per carrier investigation.',
    resolution: 'Claim denied — carrier investigation confirmed pre-existing condition at origin. Customer disputes but signed BOL without exception noted. Case closed per policy.',
    claimAmount: 3200,
    claimStatus: 'denied',
    createdAt: '2026-04-05T11:00:00Z',
    updatedAt: '2026-04-14T12:00:00Z',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 3 — CX_TEMPLATES (12 total)
//   exception_notification (2) · delivery_confirmation (2) · rate_quote (1)
//   claim_acknowledgment (2) · follow_up (2) · survey (2) · reefer_breach (1)
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_CX_TEMPLATES = [
  // ── Exception Notification ────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-001',
    templateId: 'TMPL-001',
    type: 'exception_notification',
    channel: 'email',
    name: 'Shipment Delay Notification',
    subject: 'Update on Your Shipment {{loadNumber}} — Delay Alert',
    body: `Dear {{contactName}},

We want to proactively update you regarding shipment {{loadNumber}} for your order {{poNumber}}.

We have identified a delay that will impact the original delivery window.

DELAY SUMMARY
Original ETA: {{originalETA}}
Revised ETA: {{revisedETA}}
Cause: {{delayCause}}
Current Location: {{currentLocation}}

We understand this may impact your operations and sincerely apologize for the inconvenience. Our team is actively working to minimize any further delays.

NEXT STEPS
{{nextSteps}}

Your dedicated account manager {{agentName}} is monitoring this shipment closely. Please don't hesitate to reach out if you need additional information.

Best regards,
{{agentName}}
Watchmen Logistics — {{agentPhone}}`,
    isAegisDraftable: true,
    variables: ['contactName', 'loadNumber', 'poNumber', 'originalETA', 'revisedETA', 'delayCause', 'currentLocation', 'nextSteps', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'tmpl-002',
    templateId: 'TMPL-002',
    type: 'exception_notification',
    channel: 'sms',
    name: 'Shipment Delay SMS',
    subject: null,
    body: 'Watchmen Alert: Load {{loadNumber}} delayed. New ETA: {{revisedETA}}. Cause: {{delayCause}}. Contact {{agentName}} at {{agentPhone}} for details.',
    isAegisDraftable: true,
    variables: ['loadNumber', 'revisedETA', 'delayCause', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Delivery Confirmation ─────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-003',
    templateId: 'TMPL-003',
    type: 'delivery_confirmation',
    channel: 'email',
    name: 'Delivery Confirmation Email',
    subject: 'Delivered: Shipment {{loadNumber}} — {{deliveryDate}}',
    body: `Dear {{contactName}},

We are pleased to confirm that your shipment has been successfully delivered.

DELIVERY DETAILS
Load Number: {{loadNumber}}
PO / Reference: {{poNumber}}
Delivered To: {{deliveryLocation}}
Date & Time: {{deliveryDate}} at {{deliveryTime}}
Signed By: {{receivedBy}}
Pieces Delivered: {{pieceCount}}
Weight: {{weight}} lbs

PROOF OF DELIVERY
A signed POD is available at: {{podUrl}}

Please review your order and let us know if you have any questions or if anything is missing. We appreciate your business and look forward to serving you again.

If you'd like to share feedback on this delivery, please click here: {{surveyUrl}}

Best regards,
{{agentName}}
Watchmen Logistics`,
    isAegisDraftable: true,
    variables: ['contactName', 'loadNumber', 'poNumber', 'deliveryLocation', 'deliveryDate', 'deliveryTime', 'receivedBy', 'pieceCount', 'weight', 'podUrl', 'surveyUrl', 'agentName'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'tmpl-004',
    templateId: 'TMPL-004',
    type: 'delivery_confirmation',
    channel: 'sms',
    name: 'Delivery Confirmation SMS',
    subject: null,
    body: 'Watchmen: Load {{loadNumber}} delivered {{deliveryDate}} at {{deliveryTime}} to {{deliveryLocation}}. Signed by {{receivedBy}}. Rate us: {{surveyUrl}}',
    isAegisDraftable: true,
    variables: ['loadNumber', 'deliveryDate', 'deliveryTime', 'deliveryLocation', 'receivedBy', 'surveyUrl'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Rate Quote ────────────────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-005',
    templateId: 'TMPL-005',
    type: 'rate_quote',
    channel: 'email',
    name: 'Freight Rate Quote',
    subject: 'Rate Quote {{quoteNumber}} — {{origin}} to {{destination}} | {{mode}}',
    body: `Dear {{contactName}},

Thank you for the opportunity to quote your freight. Please find our rate proposal below.

QUOTE DETAILS
Quote Number: {{quoteNumber}}
Valid Through: {{expiresAt}}
Mode: {{mode}}
Equipment: {{equipment}}
Origin: {{origin}}
Destination: {{destination}}
Weight: {{weight}} lbs
Commodity: {{commodity}}

PRICING
All-in Rate: \${{rate}}
Transit Time: {{transitDays}} business days
Fuel Surcharge: Included

SERVICE HIGHLIGHTS
• Real-time GPS tracking via Watchmen Command portal
• Proactive exception notifications
• Digital POD within 2 hours of delivery
• Dedicated account team

To accept this quote, simply reply to this email or call {{agentPhone}}.

Quote Number: {{quoteNumber}}
Best regards,
{{agentName}}
Watchmen Logistics`,
    isAegisDraftable: false,
    variables: ['contactName', 'quoteNumber', 'expiresAt', 'mode', 'equipment', 'origin', 'destination', 'weight', 'commodity', 'rate', 'transitDays', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Claim Acknowledgment ──────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-006',
    templateId: 'TMPL-006',
    type: 'claim_acknowledgment',
    channel: 'email',
    name: 'Freight Claim Acknowledgment',
    subject: 'Claim Received: {{caseId}} — {{claimType}} | {{loadNumber}}',
    body: `Dear {{contactName}},

This email confirms receipt of your freight claim. We take all claims seriously and will work diligently to resolve this matter.

CLAIM INFORMATION
Case Number: {{caseId}}
Load Number: {{loadNumber}}
Claim Type: {{claimType}}
Claim Amount: \${{claimAmount}}
Filed: {{filedDate}}

WHAT HAPPENS NEXT
1. Our claims team will review all documentation within 3 business days
2. You may be contacted for additional information (photos, invoices, etc.)
3. A carrier investigation will be initiated within 5 business days
4. Final determination issued within 30 days per 49 CFR Part 370

REQUIRED DOCUMENTATION
{{documentationList}}

Please upload supporting documents to: {{uploadUrl}}

Your claim is being handled by {{agentName}} ({{agentEmail}}). Reference {{caseId}} in all correspondence.

Watchmen Logistics Claims Department`,
    isAegisDraftable: true,
    variables: ['contactName', 'caseId', 'loadNumber', 'claimType', 'claimAmount', 'filedDate', 'documentationList', 'uploadUrl', 'agentName', 'agentEmail'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'tmpl-007',
    templateId: 'TMPL-007',
    type: 'claim_acknowledgment',
    channel: 'sms',
    name: 'Claim Acknowledgment SMS',
    subject: null,
    body: 'Watchmen Claim {{caseId}} received for load {{loadNumber}}. Amount: \${{claimAmount}}. Review within 3 business days. Contact {{agentName}}: {{agentPhone}}',
    isAegisDraftable: true,
    variables: ['caseId', 'loadNumber', 'claimAmount', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Follow Up ─────────────────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-008',
    templateId: 'TMPL-008',
    type: 'follow_up',
    channel: 'email',
    name: 'Case Follow-Up',
    subject: 'Follow-Up: Case {{caseId}} — {{subject}}',
    body: `Dear {{contactName}},

I wanted to follow up on Case {{caseId}} regarding {{subject}}.

CURRENT STATUS
{{caseStatus}}

UPDATE
{{updateDetails}}

{{#if pendingItems}}
ITEMS PENDING FROM YOU
{{pendingItems}}
{{/if}}

Please let us know if you have any questions or need additional information. We are committed to resolving this as quickly as possible.

Best regards,
{{agentName}}
{{agentTitle}}
Watchmen Logistics — {{agentPhone}}`,
    isAegisDraftable: true,
    variables: ['contactName', 'caseId', 'subject', 'caseStatus', 'updateDetails', 'pendingItems', 'agentName', 'agentTitle', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'tmpl-009',
    templateId: 'TMPL-009',
    type: 'follow_up',
    channel: 'sms',
    name: 'Follow-Up SMS',
    subject: null,
    body: 'Watchmen update on Case {{caseId}}: {{updateSummary}}. Questions? Contact {{agentName}} at {{agentPhone}}.',
    isAegisDraftable: true,
    variables: ['caseId', 'updateSummary', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Survey ────────────────────────────────────────────────────────────────
  {
    ...U,
    id: 'tmpl-010',
    templateId: 'TMPL-010',
    type: 'survey',
    channel: 'email',
    name: 'Post-Delivery Survey',
    subject: 'How did we do? 2-minute survey — Load {{loadNumber}}',
    body: `Dear {{contactName}},

Thank you for choosing Watchmen Logistics for your recent shipment ({{loadNumber}}).

We are committed to continuous improvement and would love to hear your feedback.

Rate your experience (1 = Poor, 5 = Excellent):
👉 {{surveyUrl}}

The survey takes less than 2 minutes and covers:
• On-time delivery
• Communication quality
• Damage-free delivery
• Overall satisfaction

Your feedback directly impacts how we serve you.

Thank you,
{{agentName}}
Watchmen Logistics`,
    isAegisDraftable: false,
    variables: ['contactName', 'loadNumber', 'surveyUrl', 'agentName'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'tmpl-011',
    templateId: 'TMPL-011',
    type: 'survey',
    channel: 'sms',
    name: 'Post-Delivery Survey SMS',
    subject: null,
    body: 'Watchmen: How was your delivery ({{loadNumber}})? 2-min survey: {{surveyUrl}} — Your feedback helps us improve!',
    isAegisDraftable: false,
    variables: ['loadNumber', 'surveyUrl'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── Temperature / Reefer Breach Alert ────────────────────────────────────
  {
    ...U,
    id: 'tmpl-012',
    templateId: 'TMPL-012',
    type: 'exception_notification',
    channel: 'email',
    name: 'Temperature Excursion Alert',
    subject: '⚠️ URGENT: Temperature Breach — Load {{loadNumber}} | Tracker {{trackerId}}',
    body: `Dear {{contactName}},

IMMEDIATE ACTION REQUIRED — Temperature excursion detected on your shipment.

BREACH DETAILS
Load Number: {{loadNumber}}
Tracker ID: {{trackerId}}
Current Temperature: {{currentTemp}}°F
Set Point Range: {{tempMin}}°F to {{tempMax}}°F
Breach Started: {{breachTime}}
Duration: {{breachDuration}}
Current Location: {{currentLocation}}

CARGO INFORMATION
Product: {{commodity}}
PO / Reference: {{poNumber}}

RECOMMENDED ACTIONS
{{recommendedActions}}

Our team has been alerted and is in contact with the driver. Please advise if you wish to authorize any special handling or if the shipment should be rerouted.

CONTACT IMMEDIATELY
{{agentName}} — {{agentPhone}} (mobile, 24/7)

Watchmen Command IoT Alert System`,
    isAegisDraftable: true,
    variables: ['contactName', 'loadNumber', 'trackerId', 'currentTemp', 'tempMin', 'tempMax', 'breachTime', 'breachDuration', 'currentLocation', 'commodity', 'poNumber', 'recommendedActions', 'agentName', 'agentPhone'],
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 — CUSTOMER_CONTACTS (13 total, 2-3 per customer)
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_CUSTOMER_CONTACTS = [
  // ── cu-001: Steve Madden (3 contacts) ─────────────────────────────────────
  {
    ...U,
    id: 'contact-001',
    contactId: 'CTCT-001',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    name: 'Jennifer Walsh',
    title: 'VP of Supply Chain',
    email: 'jwalsh@stevemadden.com',
    phone: '+15165550101',
    role: 'executive',
    receiveExceptions: true,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/New_York',
    notes: 'Primary escalation contact for executive-level issues',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-002',
    contactId: 'CTCT-002',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    name: 'Marcus Chen',
    title: 'Logistics Manager',
    email: 'mchen@stevemadden.com',
    phone: '+15165550102',
    role: 'primary',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: true,
    preferredChannel: 'email',
    timezone: 'America/New_York',
    notes: 'Day-to-day operations contact. Prefers morning emails.',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-003',
    contactId: 'CTCT-003',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    name: 'Dana Torres',
    title: 'AP Manager',
    email: 'dtorres@stevemadden.com',
    phone: '+15165550103',
    role: 'billing',
    receiveExceptions: false,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/New_York',
    notes: 'Billing and invoice questions only',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── cu-002: Caldwell Casting (2 contacts) ──────────────────────────────────
  {
    ...U,
    id: 'contact-004',
    contactId: 'CTCT-004',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    name: 'Robert Caldwell',
    title: 'President & CEO',
    email: 'rcaldwell@caldwellcasting.com',
    phone: '+12145550201',
    role: 'executive',
    receiveExceptions: true,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'sms',
    timezone: 'America/Chicago',
    notes: 'Owner — contact for claims and major issues only. Prefers SMS.',
    createdAt: '2026-02-01T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-005',
    contactId: 'CTCT-005',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    name: 'Kevin Marsh',
    title: 'Shipping Coordinator',
    email: 'kmarsh@caldwellcasting.com',
    phone: '+12145550202',
    role: 'operations',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: true,
    preferredChannel: 'email',
    timezone: 'America/Chicago',
    notes: 'Primary day-to-day contact for all freight matters',
    createdAt: '2026-02-01T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── cu-003: Century Aluminum (3 contacts) ─────────────────────────────────
  {
    ...U,
    id: 'contact-006',
    contactId: 'CTCT-006',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    name: 'Sandra Williams',
    title: 'Director of Procurement',
    email: 'swilliams@centuryaluminum.com',
    phone: '+13125550301',
    role: 'primary',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/Chicago',
    notes: 'Main procurement and logistics contact. Copy on all exceptions.',
    createdAt: '2026-01-20T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-007',
    contactId: 'CTCT-007',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    name: 'Derek Powell',
    title: 'Plant Manager',
    email: 'dpowell@centuryaluminum.com',
    phone: '+13125550302',
    role: 'operations',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: true,
    preferredChannel: 'sms',
    timezone: 'America/Chicago',
    notes: 'Needs immediate SMS for any delays — production line dependent',
    createdAt: '2026-01-20T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-008',
    contactId: 'CTCT-008',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    name: 'Yvonne Castillo',
    title: 'Accounts Payable',
    email: 'ycastillo@centuryaluminum.com',
    phone: '+13125550303',
    role: 'billing',
    receiveExceptions: false,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/Chicago',
    notes: 'Net-30 payment terms. Send all invoices to AP@centuryaluminum.com',
    createdAt: '2026-01-20T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── cu-004: McKesson Medical (3 contacts) ─────────────────────────────────
  {
    ...U,
    id: 'contact-009',
    contactId: 'CTCT-009',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    name: 'Patricia Morrison',
    title: 'Global Logistics Director',
    email: 'pmorrison@mckesson.com',
    phone: '+14155550401',
    role: 'executive',
    receiveExceptions: true,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/Los_Angeles',
    notes: 'Executive escalation only. CC on regulatory holds and temperature excursions.',
    createdAt: '2026-01-05T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-010',
    contactId: 'CTCT-010',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    name: 'Trevor Nguyen',
    title: 'Import Logistics Specialist',
    email: 'tnguyen@mckesson.com',
    phone: '+14155550402',
    role: 'operations',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: true,
    preferredChannel: 'email',
    timezone: 'America/Los_Angeles',
    notes: 'Primary contact for ocean and air shipments. Available 6am-6pm PT.',
    createdAt: '2026-01-05T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-011',
    contactId: 'CTCT-011',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    name: 'Audrey Kim',
    title: 'Finance Manager',
    email: 'akim@mckesson.com',
    phone: '+14155550403',
    role: 'billing',
    receiveExceptions: false,
    receiveDeliveryConfirm: false,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/Los_Angeles',
    notes: 'Net-45 payment terms. Invoice must reference PO number.',
    createdAt: '2026-01-05T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },

  // ── cu-005: Wild Fork Foods (2 contacts) ──────────────────────────────────
  {
    ...U,
    id: 'contact-012',
    contactId: 'CTCT-012',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    name: 'Steve Novick',
    title: 'VP of Operations',
    email: 'snovick@wildforkfoods.com',
    phone: '+13055550501',
    role: 'primary',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: false,
    preferredChannel: 'email',
    timezone: 'America/New_York',
    notes: 'Primary contact. Extremely temperature-sensitive cargo — always notify immediately on breach.',
    createdAt: '2026-02-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
  {
    ...U,
    id: 'contact-013',
    contactId: 'CTCT-013',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    name: 'Amy Delgado',
    title: 'Cold Chain Compliance Manager',
    email: 'adelgado@wildforkfoods.com',
    phone: '+13055550502',
    role: 'operations',
    receiveExceptions: true,
    receiveDeliveryConfirm: true,
    receiveSurveys: true,
    preferredChannel: 'sms',
    timezone: 'America/New_York',
    notes: 'IoT alerts and temperature excursion notifications MUST go to Amy via SMS immediately',
    createdAt: '2026-02-15T10:00:00Z',
    updatedAt: '2026-04-01T08:00:00Z',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5a — CX_MESSAGES (20 for unified inbox)
//   Realistic email threads: customer · carrier · internal
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_CX_MESSAGES = [
  // ── Customer Messages (8) ─────────────────────────────────────────────────
  {
    ...U,
    id: 'cxmsg-001',
    threadId: 'thr-cx-001',
    subject: 'Temperature Breach Alert — WC-10255 Wagyu Beef',
    fromEmail: 'snovick@wildforkfoods.com',
    fromName: 'Steve Novick',
    toEmail: 'ops@watchmenlogistics.com',
    body: 'Randall — just got the IoT alert. Our Wagyu beef shipment (WC-10255) is showing 28°F on the tracker. That\'s a full 38-degree breach from our -10°F setpoint. This is a $22,000+ load of premium product. What happened and what are you doing about it RIGHT NOW? I need answers in the next 30 minutes.',
    senderType: 'customer',
    customerId: 'cu-005',
    loadId: 'load-00012',
    caseId: 'case-002',
    status: 'unread',
    priority: 'urgent',
    assignedTo: 'user-001',
    tags: ['urgent', 'temperature-breach', 'claim'],
    createdAt: '2026-04-15T07:45:00Z',
    updatedAt: '2026-04-15T07:45:00Z',
  },
  {
    ...U,
    id: 'cxmsg-002',
    threadId: 'thr-cx-002',
    subject: 'Case CASE-00001 — Pallet Damage Photos Attached',
    fromEmail: 'mchen@stevemadden.com',
    fromName: 'Marcus Chen',
    toEmail: 'claims@watchmenlogistics.com',
    body: 'Hi Maria, as requested I\'ve attached 14 photos of the damaged pallets from load WC-10247. You can clearly see the crush damage on pallets 7 and 12. The SHOE-4401 Maxima Platform boxes are completely destroyed — those were $120/unit product. I also have the receiver\'s signed exception notation on the BOL. When can we expect a claims adjuster?',
    senderType: 'customer',
    customerId: 'cu-001',
    loadId: 'load-00005',
    caseId: 'case-001',
    status: 'unread',
    priority: 'high',
    assignedTo: 'user-002',
    tags: ['damage', 'claim', 'photos'],
    createdAt: '2026-04-14T11:30:00Z',
    updatedAt: '2026-04-14T11:30:00Z',
  },
  {
    ...U,
    id: 'cxmsg-003',
    threadId: 'thr-cx-003',
    subject: 'Production Line Stopped — Need Rail Update NOW',
    fromEmail: 'dpowell@centuryaluminum.com',
    fromName: 'Derek Powell',
    toEmail: 'dispatch@watchmenlogistics.com',
    body: 'Our entire Line 3 is down waiting for the aluminum extrusions on WC-RAIL-004. We were promised delivery yesterday. Louisville is now saying "mechanical hold" but nobody will give us a specific ETA. I have 40 workers standing by. Every hour costs us $18,000 in lost production. What is the EXACT timeline?',
    senderType: 'customer',
    customerId: 'cu-003',
    loadId: 'load-00020',
    caseId: 'case-004',
    status: 'unread',
    priority: 'urgent',
    assignedTo: 'user-001',
    tags: ['urgent', 'rail-delay', 'production-impact'],
    createdAt: '2026-04-15T09:15:00Z',
    updatedAt: '2026-04-15T09:15:00Z',
  },
  {
    ...U,
    id: 'cxmsg-004',
    threadId: 'thr-cx-004',
    subject: 'FDA Hold Status — WC-OCN-006 Pharmaceutical Cargo',
    fromEmail: 'tnguyen@mckesson.com',
    fromName: 'Trevor Nguyen',
    toEmail: 'ocean@watchmenlogistics.com',
    body: 'Hi, I need a daily update on WC-OCN-006 at Savannah. Our QA team is tracking this closely — it\'s Class 6.1 pharmaceutical raw material needed for our Chatham, NJ plant. The manufacturing schedule has already been adjusted twice. FDA inspection is April 19 — is everything lined up with the customs broker? Who is our contact at the Savannah port?',
    senderType: 'customer',
    customerId: 'cu-004',
    loadId: 'load-00041',
    caseId: 'case-007',
    status: 'read',
    priority: 'high',
    assignedTo: 'user-001',
    tags: ['customs-hold', 'fda', 'pharmaceutical'],
    createdAt: '2026-04-15T10:00:00Z',
    updatedAt: '2026-04-15T10:30:00Z',
  },
  {
    ...U,
    id: 'cxmsg-005',
    threadId: 'thr-cx-005',
    subject: 'Q2 Lane Quote Request — Dallas to Detroit Intermodal',
    fromEmail: 'kmarsh@caldwellcasting.com',
    fromName: 'Kevin Marsh',
    toEmail: 'sales@watchmenlogistics.com',
    body: 'Hi Randall — we\'re expanding our Detroit operations and need intermodal capacity on the Dallas to Detroit lane. Looking at 8-12 loads per month, 53ft containers, cast iron automotive components averaging 42-44k lbs. We\'d want to lock a Q2/Q3 contract rate. Can you put together a proposal? Transit time is critical — we need 4 days max.',
    senderType: 'customer',
    customerId: 'cu-002',
    loadId: null,
    caseId: 'case-008',
    status: 'read',
    priority: 'medium',
    assignedTo: 'user-001',
    tags: ['new-business', 'quote-request'],
    createdAt: '2026-04-16T13:00:00Z',
    updatedAt: '2026-04-16T15:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-006',
    threadId: 'thr-cx-006',
    subject: 'Per Diem Waiver Request — HLXU9876543',
    fromEmail: 'snovick@wildforkfoods.com',
    fromName: 'Steve Novick',
    toEmail: 'ops@watchmenlogistics.com',
    body: 'Maria, we never received a notification that the LFD on HLXU9876543 was approaching. Now we\'re getting billed 3 days of per diem at $150/day. This is $450 we shouldn\'t owe. Our SLA agreement states we\'re supposed to get 72-hour advance notice. Can you review your notification logs and issue a credit?',
    senderType: 'customer',
    customerId: 'cu-005',
    loadId: 'load-00028',
    caseId: 'case-009',
    status: 'read',
    priority: 'medium',
    assignedTo: 'user-002',
    tags: ['per-diem', 'billing-dispute'],
    createdAt: '2026-04-16T09:30:00Z',
    updatedAt: '2026-04-16T11:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-007',
    threadId: 'thr-cx-007',
    subject: 'Re: Invoice INV-4418 — Duplicate Billing Confirmed',
    fromEmail: 'akim@mckesson.com',
    fromName: 'Audrey Kim',
    toEmail: 'billing@watchmenlogistics.com',
    body: 'Team — confirming receipt of the credit memo for $11,760 on the duplicate invoice issue. That has been posted to our AR system. Going forward, please ensure your billing system flags duplicate waybill numbers before invoicing. We will be conducting a quarterly audit of freight invoices starting Q3. Please reach out if you need our preferred vendor portal instructions.',
    senderType: 'customer',
    customerId: 'cu-004',
    loadId: 'load-00036',
    caseId: 'case-012',
    status: 'read',
    priority: 'low',
    assignedTo: 'user-002',
    tags: ['billing', 'resolved'],
    createdAt: '2026-04-13T16:00:00Z',
    updatedAt: '2026-04-13T16:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-008',
    threadId: 'thr-cx-008',
    subject: 'Vendor Documentation Request — Annual Carrier Review',
    fromEmail: 'swilliams@centuryaluminum.com',
    fromName: 'Sandra Williams',
    toEmail: 'compliance@watchmenlogistics.com',
    body: 'Hi — our procurement team is conducting the annual carrier vendor review. We need updated insurance certificates (minimum $1M cargo, $2M GL), MC numbers, FMCSA safety ratings, and W-9 forms for all carriers operating on Century Aluminum lanes. Please compile and send by April 25. If any carrier is rated conditional or has open safety complaints, please flag separately.',
    senderType: 'customer',
    customerId: 'cu-003',
    loadId: null,
    caseId: 'case-010',
    status: 'read',
    priority: 'low',
    assignedTo: 'user-002',
    tags: ['compliance', 'documentation'],
    createdAt: '2026-04-10T10:30:00Z',
    updatedAt: '2026-04-12T09:00:00Z',
  },

  // ── Carrier Messages (6) ──────────────────────────────────────────────────
  {
    ...U,
    id: 'cxmsg-009',
    threadId: 'thr-cx-009',
    subject: 'Breakdown Report — Load WC-10255 | Driver Ray Thompson',
    fromEmail: 'dispatch@swifttransport.com',
    fromName: 'Swift Dispatch',
    toEmail: 'ops@watchmenlogistics.com',
    body: 'Watchmen team — driver Ray Thompson on load WC-10255 is experiencing a reefer unit malfunction. Carrier unit failed to maintain setpoint. Driver is currently at mile marker 142 on I-75 southbound near Valdosta, GA. We have dispatched a reefer mechanic and backup unit. ETA for repair or swap: approximately 3 hours. Advising you per SOP.',
    senderType: 'carrier',
    customerId: 'cu-005',
    loadId: 'load-00012',
    caseId: 'case-002',
    status: 'unread',
    priority: 'urgent',
    assignedTo: 'user-001',
    tags: ['breakdown', 'reefer-failure', 'urgent'],
    createdAt: '2026-04-15T06:30:00Z',
    updatedAt: '2026-04-15T06:30:00Z',
  },
  {
    ...U,
    id: 'cxmsg-010',
    threadId: 'thr-cx-010',
    subject: 'Rate Confirmation — WC-10262 | OTR NY to Dallas',
    fromEmail: 'ratecon@schneider.com',
    fromName: 'Schneider National',
    toEmail: 'dispatch@watchmenlogistics.com',
    body: 'Please find attached the signed rate confirmation for load WC-10262. Confirmed rate: $3,680 all-in. Driver Bobby Jackson, Unit WL-9121. Pickup April 16 at 0600 from Long Island City, NY. Delivery April 18 by 1400 to Dallas DC. Any changes to route or delivery window require 4-hour advance notice per our SOP.',
    senderType: 'carrier',
    customerId: 'cu-001',
    loadId: 'load-00005',
    caseId: null,
    status: 'read',
    priority: 'medium',
    assignedTo: null,
    tags: ['rate-con', 'confirmed'],
    createdAt: '2026-04-15T14:00:00Z',
    updatedAt: '2026-04-15T14:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-011',
    threadId: 'thr-cx-011',
    subject: 'NS Railroad Update — Train Hold Louisville KY',
    fromEmail: 'intermodal@nscorp.com',
    fromName: 'NS Intermodal Operations',
    toEmail: 'rail@watchmenlogistics.com',
    body: 'Update on train I-DALS1-05 holding at Louisville. Hot bearing on car NS-662841 confirmed — bearing replaced at 0200 local. Train is cleared to depart at 0600 this morning. Revised ETA Detroit Livernois: April 17 at 2100. This represents approximately 27 hours delay from original schedule. Updated BOL will be transmitted via EDI within 2 hours.',
    senderType: 'carrier',
    customerId: 'cu-002',
    loadId: 'load-00020',
    caseId: 'case-004',
    status: 'unread',
    priority: 'high',
    assignedTo: 'user-001',
    tags: ['rail', 'delay-update'],
    createdAt: '2026-04-15T05:45:00Z',
    updatedAt: '2026-04-15T05:45:00Z',
  },
  {
    ...U,
    id: 'cxmsg-012',
    threadId: 'thr-cx-012',
    subject: 'J.B. Hunt Damage Claim Response — Load WC-10270',
    fromEmail: 'claims@jbhunt.com',
    fromName: 'J.B. Hunt Claims Dept',
    toEmail: 'claims@watchmenlogistics.com',
    body: 'In response to Case CASE-00003 for load WC-10270: Our claims investigator reviewed the shock data. The 4.2G event at hour 1 is within industry norms for flatbed loading operations. We maintain that the cracking occurred due to improper securing of the cast iron components by the shipper. Please provide the shipper\'s loading photos and strapping documentation. Without this, we cannot accept liability. Reference our claim: JBH-CLM-2026-0891.',
    senderType: 'carrier',
    customerId: 'cu-002',
    loadId: 'load-00008',
    caseId: 'case-003',
    status: 'unread',
    priority: 'high',
    assignedTo: 'user-002',
    tags: ['claim', 'carrier-dispute'],
    createdAt: '2026-04-16T11:00:00Z',
    updatedAt: '2026-04-16T11:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-013',
    threadId: 'thr-cx-013',
    subject: 'Werner ETA Confirmation — WC-10265 Tucson AZ',
    fromEmail: 'dispatch@werner.com',
    fromName: 'Werner Enterprises Dispatch',
    toEmail: 'ops@watchmenlogistics.com',
    body: 'Driver Frank Mueller on load WC-10265 confirms ETA to Tucson AZ distribution center is 1400 today. He will call ahead to the receiver 1 hour prior. Reminder: this is the McKesson Medical load — PHARMA sticker on trailer, temperature log required at delivery. Driver has all required paperwork. Any special receiving instructions?',
    senderType: 'carrier',
    customerId: 'cu-004',
    loadId: 'load-00031',
    caseId: null,
    status: 'read',
    priority: 'low',
    assignedTo: null,
    tags: ['eta-confirmation'],
    createdAt: '2026-04-15T08:00:00Z',
    updatedAt: '2026-04-15T08:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-014',
    threadId: 'thr-cx-014',
    subject: 'Missing Pallet Trace — LTL PRO# 456789012',
    fromEmail: 'tracing@xpologistics.com',
    fromName: 'XPO Logistics Tracing',
    toEmail: 'claims@watchmenlogistics.com',
    body: 'In response to your trace request for PRO# 456789012: Our hub scan shows pallet 12 (HUID 556-Q2741) last scanned at Cleveland Gateway hub at 21:42 on April 14. It was flagged for re-sort due to damaged label. It is being held at Cleveland pending re-labeling and will be on the next available outbound trailer to Dallas — estimated delivery April 18. Tracking will resume once re-labeled.',
    senderType: 'carrier',
    customerId: 'cu-002',
    loadId: 'load-00025',
    caseId: 'case-013',
    status: 'unread',
    priority: 'high',
    assignedTo: 'user-001',
    tags: ['lost-freight', 'trace', 'ltl'],
    createdAt: '2026-04-16T14:30:00Z',
    updatedAt: '2026-04-16T14:30:00Z',
  },

  // ── Internal Messages (6) ─────────────────────────────────────────────────
  {
    ...U,
    id: 'cxmsg-015',
    threadId: 'thr-cx-015',
    subject: 'AEGIS Alert: 3 Loads at SLA Risk Today',
    fromEmail: 'aegis@watchmencommand.com',
    fromName: 'AEGIS System',
    toEmail: 'ops@watchmenlogistics.com',
    body: 'Automated AEGIS SLA Monitoring Report — April 15, 2026\n\nThe following loads are approaching SLA breach thresholds:\n\n1. WC-10255 (Wild Fork Foods) — Reefer failure in transit. CRITICAL. Customer notified: NO. SLA window closes: 2h 15m\n\n2. WC-RAIL-004 (Century Aluminum) — Rail mechanical delay 28h. HIGH. Customer notified: NO. Production impact confirmed.\n\n3. WC-OCN-006 (McKesson Medical) — Customs hold 3+ days. HIGH. Customer last contacted: 24h ago.\n\nRecommended actions: Notify Wild Fork immediately via phone. Send rail update to Century Aluminum Derek Powell via SMS.',
    senderType: 'internal',
    customerId: null,
    loadId: null,
    caseId: null,
    status: 'unread',
    priority: 'urgent',
    assignedTo: 'user-001',
    tags: ['aegis', 'sla-alert', 'automated'],
    createdAt: '2026-04-15T08:00:00Z',
    updatedAt: '2026-04-15T08:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-016',
    threadId: 'thr-cx-016',
    subject: 'Claim Case-002 — Internal Status Note',
    fromEmail: 'user-001@watchmenlogistics.com',
    fromName: 'Randall Roberson',
    toEmail: 'team@watchmenlogistics.com',
    body: 'Team — quick update on Wild Fork temperature claim (CASE-00002).\n\nSpoke with Steve Novick 20 mins ago. He is furious. The $22,800 Wagyu load is a total loss per their food safety standards — any temp excursion above 10°F makes the product unsellable.\n\nCarrier Swift is claiming the reefer unit failure was a "manufacturer defect" not driver error. They have requested 5 business days to investigate.\n\nI am proceeding with our carrier insurance claim. Maria — can you pull all IoT data for IOTK-001 and package the temperature logs for the claim file?\n\nI will call the customer back with status at 2pm today.',
    senderType: 'internal',
    customerId: 'cu-005',
    loadId: 'load-00012',
    caseId: 'case-002',
    status: 'read',
    priority: 'high',
    assignedTo: 'user-002',
    tags: ['internal', 'claim-update'],
    createdAt: '2026-04-15T11:30:00Z',
    updatedAt: '2026-04-15T11:30:00Z',
  },
  {
    ...U,
    id: 'cxmsg-017',
    threadId: 'thr-cx-017',
    subject: 'Monthly CX KPI Review — April 2026',
    fromEmail: 'user-001@watchmenlogistics.com',
    fromName: 'Randall Roberson',
    toEmail: 'team@watchmenlogistics.com',
    body: 'Team — sharing April CX metrics:\n\nOpen Cases: 8 (3 urgent)\nResolved This Month: 6\nAvg Resolution Time: 2.4 days\nCustomer Satisfaction (survey avg): 4.2/5.0\nClaims Filed: 5 | Approved: 2 | Pending: 3\nSLA Compliance Rate: 94%\n\nHighlights:\n• McKesson duplicate billing resolved quickly — credit issued same day\n• Wild Fork temperature claim is our largest open liability ($24.5K)\n• Century Aluminum rail delay — Q2 at-risk account, needs executive outreach\n\nAction items:\n1. Call Century Aluminum Sandra Williams this week — at-risk relationship\n2. Review reefer carrier vetting — 2 incidents this quarter\n3. Implement proactive LFD notification workflow (AEGIS-4)',
    senderType: 'internal',
    customerId: null,
    loadId: null,
    caseId: null,
    status: 'read',
    priority: 'medium',
    assignedTo: null,
    tags: ['internal', 'kpi', 'monthly-review'],
    createdAt: '2026-04-16T09:00:00Z',
    updatedAt: '2026-04-16T09:00:00Z',
  },
  {
    ...U,
    id: 'cxmsg-018',
    threadId: 'thr-cx-018',
    subject: 'Case-003 — Need Shipper Loading Photos ASAP',
    fromEmail: 'user-002@watchmenlogistics.com',
    fromName: 'Maria Rodriguez',
    toEmail: 'user-001@watchmenlogistics.com',
    body: 'Randall — JBH just responded to the Caldwell Casting shock damage claim. They\'re disputing liability and want shipper loading photos/strapping docs before they\'ll accept. Do we have those on file? The Caldwell team needs to provide: 1) Photos of how the cast components were secured at origin, 2) BOL noting any shipper load and count exceptions, 3) Crane/forklift loading records if available. Can you reach Kevin Marsh at Caldwell and request this week? The 30-day claim window closes May 14.',
    senderType: 'internal',
    customerId: 'cu-002',
    loadId: 'load-00008',
    caseId: 'case-003',
    status: 'unread',
    priority: 'high',
    assignedTo: 'user-001',
    tags: ['internal', 'claim-action'],
    createdAt: '2026-04-16T13:30:00Z',
    updatedAt: '2026-04-16T13:30:00Z',
  },
  {
    ...U,
    id: 'cxmsg-019',
    threadId: 'thr-cx-019',
    subject: 'New Case Opened: CASE-00008 — Caldwell Quote Request',
    fromEmail: 'aegis@watchmencommand.com',
    fromName: 'AEGIS System',
    toEmail: 'sales@watchmenlogistics.com',
    body: 'AEGIS has auto-opened Case CASE-00008 based on incoming message from Kevin Marsh at Caldwell Casting.\n\nType: New Business Inquiry\nCustomer: Caldwell Casting (cu-002)\nLane: Dallas → Detroit\nMode: Intermodal (53ft containers)\nVolume: 8-12 loads/month\nTransit Requirement: ≤4 days\n\nSuggested next steps:\n1. Pull NS / BNSF intermodal rates for DAL-DTW lane\n2. Verify capacity with J.B. Hunt or Schneider intermodal\n3. Respond within 24 hours (customer SLA)\n4. Quote via QT flow using Intermodal template\n\nAssigned to: Randall Roberson',
    senderType: 'internal',
    customerId: 'cu-002',
    loadId: null,
    caseId: 'case-008',
    status: 'read',
    priority: 'medium',
    assignedTo: 'user-001',
    tags: ['aegis', 'auto-case', 'new-business'],
    createdAt: '2026-04-16T13:05:00Z',
    updatedAt: '2026-04-16T13:05:00Z',
  },
  {
    ...U,
    id: 'cxmsg-020',
    threadId: 'thr-cx-020',
    subject: 'SLA Config Update — Wild Fork Foods',
    fromEmail: 'user-001@watchmenlogistics.com',
    fromName: 'Randall Roberson',
    toEmail: 'user-002@watchmenlogistics.com',
    body: 'Maria — I\'ve updated the SLA config for Wild Fork Foods in the system following the temperature breach incident. Changes made:\n\n• Delivery window: tightened from ±4h to ±2h\n• Notification preference: changed from "review_first" to "auto_send" for all exceptions\n• Added Amy Delgado (Cold Chain Compliance) to exception notification list via SMS\n• Escalation email set to Steve Novick (VP Ops)\n\nAEGIS will now auto-notify Wild Fork immediately on any sensor breach or SLA risk — no manual review step.\n\nAlso please note: their next big load (frozen seafood, WC-OCN-003 inbound via Miami) arrives April 22. Make sure the drayage team is ready same day.',
    senderType: 'internal',
    customerId: 'cu-005',
    loadId: null,
    caseId: null,
    status: 'read',
    priority: 'medium',
    assignedTo: null,
    tags: ['internal', 'sla-update', 'process-change'],
    createdAt: '2026-04-15T16:00:00Z',
    updatedAt: '2026-04-15T16:00:00Z',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5b — SURVEYS (10 post-delivery responses)
// ─────────────────────────────────────────────────────────────────────────────
export const SEED_SURVEYS = [
  {
    ...U,
    id: 'survey-001',
    surveyId: 'SRV-001',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    contactId: 'contact-002',
    contactName: 'Marcus Chen',
    loadId: 'load-00003',
    loadNumber: 'WC-10247',
    surveyType: 'delivery',
    status: 'completed',
    rating: 5,
    ratings: { onTime: 5, communication: 5, damageFree: 5, overall: 5 },
    comments: 'Excellent service as always. Driver was professional and the load arrived on time without any issues. The real-time tracking through the portal is very helpful for our DC planning.',
    nps: 10,
    submittedAt: '2026-04-10T14:30:00Z',
    createdAt: '2026-04-10T14:00:00Z',
    updatedAt: '2026-04-10T14:30:00Z',
  },
  {
    ...U,
    id: 'survey-002',
    surveyId: 'SRV-002',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    contactId: 'contact-010',
    contactName: 'Trevor Nguyen',
    loadId: 'load-00031',
    loadNumber: 'WC-10265',
    surveyType: 'delivery',
    status: 'completed',
    rating: 4,
    ratings: { onTime: 4, communication: 5, damageFree: 5, overall: 4 },
    comments: 'Good delivery. Communication from the dispatch team was excellent. The only issue was the driver called 45 minutes before arrival rather than the 1-hour we requested. Temperature logs were provided promptly which we appreciate for pharma compliance.',
    nps: 8,
    submittedAt: '2026-04-12T10:00:00Z',
    createdAt: '2026-04-12T09:30:00Z',
    updatedAt: '2026-04-12T10:00:00Z',
  },
  {
    ...U,
    id: 'survey-003',
    surveyId: 'SRV-003',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    contactId: 'contact-007',
    contactName: 'Derek Powell',
    loadId: 'load-00015',
    loadNumber: 'WC-10260',
    surveyType: 'delivery',
    status: 'completed',
    rating: 5,
    ratings: { onTime: 5, communication: 4, damageFree: 5, overall: 5 },
    comments: 'Delivery was on time and the flatbed was tarped correctly — this is not always the case with other brokers. Aluminum coils were pristine. Driver helped with unloading coordination. Very happy with this shipment.',
    nps: 9,
    submittedAt: '2026-04-09T16:00:00Z',
    createdAt: '2026-04-09T15:00:00Z',
    updatedAt: '2026-04-09T16:00:00Z',
  },
  {
    ...U,
    id: 'survey-004',
    surveyId: 'SRV-004',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    contactId: 'contact-005',
    contactName: 'Kevin Marsh',
    loadId: 'load-00008',
    loadNumber: 'WC-10270',
    surveyType: 'delivery',
    status: 'completed',
    rating: 2,
    ratings: { onTime: 4, communication: 3, damageFree: 1, overall: 2 },
    comments: 'Delivery was on time but we found 3 cast components cracked upon receipt. Shock sensor data you sent us shows a 4.2G impact. This is unacceptable for precision machined parts. We have filed a claim and expect full resolution. Lowering our score until this is resolved.',
    nps: 2,
    submittedAt: '2026-04-15T12:00:00Z',
    createdAt: '2026-04-15T11:00:00Z',
    updatedAt: '2026-04-15T12:00:00Z',
  },
  {
    ...U,
    id: 'survey-005',
    surveyId: 'SRV-005',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    contactId: 'contact-012',
    contactName: 'Steve Novick',
    loadId: 'load-00033',
    loadNumber: 'WC-10278',
    surveyType: 'delivery',
    status: 'completed',
    rating: 4,
    ratings: { onTime: 5, communication: 4, damageFree: 4, overall: 4 },
    comments: 'Good delivery overall. Temperature stayed within setpoint throughout and the product was in perfect condition. We did notice one box of shrimp was damp on the exterior suggesting some condensation during transit, but product quality was fine.',
    nps: 7,
    submittedAt: '2026-04-08T13:00:00Z',
    createdAt: '2026-04-08T12:00:00Z',
    updatedAt: '2026-04-08T13:00:00Z',
  },
  {
    ...U,
    id: 'survey-006',
    surveyId: 'SRV-006',
    customerId: 'cu-001',
    customerName: 'Steve Madden',
    contactId: 'contact-002',
    contactName: 'Marcus Chen',
    loadId: 'load-00018',
    loadNumber: 'WC-10251',
    surveyType: 'delivery',
    status: 'completed',
    rating: 3,
    ratings: { onTime: 2, communication: 4, damageFree: 5, overall: 3 },
    comments: 'Load arrived 4 hours late. Watchmen did proactively notify us of the delay which helped us reschedule dock crew, so communication was good. But the late arrival cost us overtime. Product was perfect. Please work with carrier on dispatch timing.',
    nps: 5,
    submittedAt: '2026-04-05T09:00:00Z',
    createdAt: '2026-04-05T08:00:00Z',
    updatedAt: '2026-04-05T09:00:00Z',
  },
  {
    ...U,
    id: 'survey-007',
    surveyId: 'SRV-007',
    customerId: 'cu-004',
    customerName: 'McKesson Medical',
    contactId: 'contact-010',
    contactName: 'Trevor Nguyen',
    loadId: 'load-00036',
    loadNumber: 'WC-10262',
    surveyType: 'delivery',
    status: 'completed',
    rating: 5,
    ratings: { onTime: 5, communication: 5, damageFree: 5, overall: 5 },
    comments: 'Outstanding service on this air freight shipment. When our surgical instruments needed to move urgently from Frankfurt, Watchmen had it coordinated within 2 hours. Delivered to JFK on time. The customs clearance was handled seamlessly. This is the level of service we expect from our critical supply partners.',
    nps: 10,
    submittedAt: '2026-04-02T17:00:00Z',
    createdAt: '2026-04-02T16:00:00Z',
    updatedAt: '2026-04-02T17:00:00Z',
  },
  {
    ...U,
    id: 'survey-008',
    surveyId: 'SRV-008',
    customerId: 'cu-003',
    customerName: 'Century Aluminum',
    contactId: 'contact-006',
    contactName: 'Sandra Williams',
    loadId: 'load-00020',
    loadNumber: 'WC-10262',
    surveyType: 'delivery',
    status: 'completed',
    rating: 1,
    ratings: { onTime: 1, communication: 2, damageFree: 5, overall: 1 },
    comments: 'The rail delay was 28 hours and cost us $504,000 in lost production. While I understand mechanical issues happen, we were not proactively notified for the first 6 hours. We found out from our own production team calling us. This is a critical account relationship issue that needs executive attention.',
    nps: 1,
    submittedAt: '2026-04-15T18:00:00Z',
    createdAt: '2026-04-15T17:00:00Z',
    updatedAt: '2026-04-15T18:00:00Z',
  },
  {
    ...U,
    id: 'survey-009',
    surveyId: 'SRV-009',
    customerId: 'cu-005',
    customerName: 'Wild Fork Foods',
    contactId: 'contact-013',
    contactName: 'Amy Delgado',
    loadId: 'load-00041',
    loadNumber: 'WC-10285',
    surveyType: 'delivery',
    status: 'completed',
    rating: 5,
    ratings: { onTime: 5, communication: 5, damageFree: 5, overall: 5 },
    comments: 'Perfect execution on this reefer run. Temperature logs showed -10°F maintained throughout. Driver was very professional with our cold chain receiving process. POD was in our email within 30 minutes of delivery. This is exactly what we need.',
    nps: 10,
    submittedAt: '2026-04-07T11:00:00Z',
    createdAt: '2026-04-07T10:00:00Z',
    updatedAt: '2026-04-07T11:00:00Z',
  },
  {
    ...U,
    id: 'survey-010',
    surveyId: 'SRV-010',
    customerId: 'cu-002',
    customerName: 'Caldwell Casting',
    contactId: 'contact-005',
    contactName: 'Kevin Marsh',
    loadId: 'load-00025',
    loadNumber: 'WC-10251',
    surveyType: 'delivery',
    status: 'completed',
    rating: 4,
    ratings: { onTime: 4, communication: 5, damageFree: 4, overall: 4 },
    comments: 'This intermodal run to Atlanta went smoothly. The transit time of 2 days was great. One of the cast components had minor surface scuffs but nothing structural — we accepted without exception. The load tracking portal is really useful for our production scheduling. Happy overall.',
    nps: 8,
    submittedAt: '2026-04-03T14:00:00Z',
    createdAt: '2026-04-03T13:00:00Z',
    updatedAt: '2026-04-03T14:00:00Z',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY
// ─────────────────────────────────────────────────────────────────────────────
export const CX_SEED_SUMMARY = {
  cx_cases: SEED_CX_CASES.length,
  cx_templates: SEED_CX_TEMPLATES.length,
  customer_contacts: SEED_CUSTOMER_CONTACTS.length,
  cx_messages: SEED_CX_MESSAGES.length,
  surveys: SEED_SURVEYS.length,
  total: SEED_CX_CASES.length + SEED_CX_TEMPLATES.length + SEED_CUSTOMER_CONTACTS.length + SEED_CX_MESSAGES.length + SEED_SURVEYS.length,
};
